/**
 * Translations index.
 *
 * - English (default) is statically imported so the very first paint has
 *   strings without an async wait.
 * - Spanish ships in a separate chunk loaded on demand by LanguageContext
 *   when the user (or their saved preference) selects ES.
 *
 * Public API kept compatible with previous default export:
 *   import { translations } from "@/i18n/translations";
 *   translations.en  // always available (eager)
 *   translations.es  // populated after loadLanguage('es')
 */
import en from "./translations.en";

export type Language = "es" | "en";
export const DEFAULT_LANGUAGE: Language = "en";
export const AVAILABLE_LANGUAGES: Language[] = ["en", "es"];

export type TranslationKeys = keyof typeof en;

// `es` starts mirrored to `en` so any sync lookup before the ES chunk loads
// falls back to English (better than blank UI). Replaced in place on load.
const store: { en: Record<string, string>; es: Record<string, string> } = {
  en: en as unknown as Record<string, string>,
  es: en as unknown as Record<string, string>,
};

export const translations = store as unknown as {
  en: typeof en;
  es: typeof en;
};

let esPromise: Promise<void> | null = null;

/**
 * Dynamically load the Spanish dictionary. Idempotent.
 * Resolves immediately if already loaded.
 */
export function loadLanguage(lang: Language): Promise<void> {
  if (lang === "en") return Promise.resolve();
  if (esPromise) return esPromise;
  esPromise = import("./translations.es")
    .then((mod) => {
      store.es = mod.default as unknown as Record<string, string>;
    })
    .catch((e) => {
      console.warn("[i18n] failed to load ES translations", e);
    });
  return esPromise;
}

export const getTranslationKeys = () =>
  Object.keys(en) as TranslationKeys[];
